import React, { useContext, useState } from 'react';
import { Card, ListGroup, Button, Modal } from 'react-bootstrap';
import { getStoryMetadataValue } from './utility/MetadataFetcher';
import { WalkDataContext } from './hooks/walkDataContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo} from '@fortawesome/free-solid-svg-icons';

const ResetModal = ({ show, handleClose, handleReset }) => (
 <Modal show={show} onHide={handleClose} className="reset-progress-modal">
   <Modal.Header closeButton className="reset-modal-header">
     <Modal.Title className="reset-modal-title">Reset progress?</Modal.Title>
   </Modal.Header>
   <Modal.Body id="reset-modal-description" className="reset-modal-body">
     <i className="fas fa-exclamation-triangle text-warning me-2" aria-hidden="true"></i>
     All your progress will be lost.
   </Modal.Body>
   <Modal.Footer className="reset-modal-footer">
     <Button variant="secondary" onClick={handleClose} className="reset-modal-cancel">
       No
     </Button>
     <Button variant="danger" onClick={handleReset} className="reset-modal-confirm" aria-describedby="reset-modal-description">
      Yes, reset
     </Button>
   </Modal.Footer>
 </Modal>
);

const StoryDetails = ({metas__, language__}) => {
 const [showResetModal, setShowResetModal] = useState(false);
 const { storyData, storyFeatures, storyMetas, language, contentState, extractStates} = useContext(WalkDataContext);

 const storyTitle = getStoryMetadataValue(storyMetas, language, 'storyMeta_title', '-- ohne Titel --');
 const storySubTitle = getStoryMetadataValue(storyMetas, language, 'storyMeta_subtitle', '');
 const storyAuthor = getStoryMetadataValue(storyMetas, 1, 'storyMeta_author', '');
 const storyCover = getStoryMetadataValue(storyMetas, language, 'storyMeta_cover', '/assets/illustrations/placeholder-cover.jpg');
 const storyDescription = getStoryMetadataValue(storyMetas, language, 'storyMeta_description', '', true);
 const storyDuration = getStoryMetadataValue(storyMetas, 1, 'storyMeta_duration', '');
 const storyReleasedate = getStoryMetadataValue(storyMetas, 1, 'storyMeta_releasedate', '');

 const areStatesEqual = (contentState, storyDataStates) => {
  // Prüfen, ob die Arrays die gleiche Länge haben
  if (contentState.length !== storyDataStates.length) {
    return false;
  }

  return contentState.every(contentItem => {
    const storyDataItem = storyDataStates.find(item => item.slug === contentItem.slug);
    
    if (!storyDataItem) return false;
    
    // Vergleich der relevanten Eigenschaften
    return contentItem.type === storyDataItem.type &&
           contentItem.slug === storyDataItem.slug &&
           contentItem.state === storyDataItem.state;
  });
};

 function formatDate(inputDate) {
   const date = new Date(inputDate);
   
   const monthNames = [
     "Januar", "Februar", "März", "April", "Mai", "Juni",
     "Juli", "August", "September", "Oktober", "November", "Dezember"
   ];

   const month = monthNames[date.getMonth()];
   const year = date.getFullYear();

   return `${month} ${year}`;
 }

 const formatMinutes = (minutes) => {
   if (minutes < 60) {
     return `etwa ${Math.round(minutes)} Minuten`;
   } else {
     const hours = Math.floor(minutes / 60);
     const remainingMinutes = minutes % 60;

     let roundedMinutes;
     if (remainingMinutes === 0) {
       return `etwa ${hours} Stunde${hours > 1 ? 'n' : ''}`;
     } else if (remainingMinutes <= 15) {
       roundedMinutes = 15;
     } else if (remainingMinutes <= 30) {
       roundedMinutes = 30;
     } else if (remainingMinutes <= 45) {
       roundedMinutes = 45;
     } else if (remainingMinutes <= 60) {
       roundedMinutes = 0;
     }

     if (roundedMinutes === 0) {
       return `etwa ${hours + 1} Stunde${hours + 1 > 1 ? 'n' : ''}`;
     } else if (roundedMinutes === 30) {
       return `etwa ${hours}.5h`;
     } else {
       return `etwa ${hours}h ${roundedMinutes} min`;
     }
   }
 };

 const handleReset = () => {
   localStorage.removeItem(`progress_${storyData.story.slug}`);
   window.location.reload();
 };

 if (!storyData?.story) {
  return (
    <div className="card-container d-flex justify-content-center align-items-center story-loading-container" aria-live="polite" aria-busy="true">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading story details...</span>
      </div>
    </div>
  );
}

 return (
   <div className="card-container story-details-container">
     <Card className="story-details-card">
       <Card.Img variant="top" src={storyCover} alt={`${storyTitle} cover image`} className='card-coverimage' /><br />
       <Card.Body>
         {storyAuthor &&
           <p className='track-description story-author' aria-label={`Author: ${storyAuthor}`}>
             <span aria-hidden="true">{storyAuthor}</span>
           </p>
         }
         <Card.Title as="h1" className="title-page title-storydetails">{storyTitle}</Card.Title>
         {storySubTitle &&
           <p className='track-description story-subtitle pb-3' aria-label={`Subtitle: ${storySubTitle}`}>
             <span aria-hidden="true">{storySubTitle}</span>
           </p>
         }
         {storyDescription && <Card.Text>
           <span className='track-description story-description-content' dangerouslySetInnerHTML={storyDescription} />
         </Card.Text>}
       </Card.Body>
       <ListGroup className="list-group-flush story-meta-list">
         {(storyDuration || storyReleasedate) &&(
           <ListGroup.Item className="story-meta-item">
             {storyDuration && (
               <span className='story-duration-display' aria-label={`Duration: ${formatMinutes(storyDuration)}`}>
                 <span aria-hidden="true">{formatMinutes(storyDuration)}</span>
               </span>
             )}
             {storyReleasedate && (
               <span className='story-release-display ml-150' aria-label={`Released: ${formatDate(storyReleasedate)}`}>
                 <span aria-hidden="true">{formatDate(storyReleasedate)}</span>
               </span>
             )}
           </ListGroup.Item>
         )}
       </ListGroup>
     </Card>

     {!areStatesEqual(contentState,extractStates(storyData)) && (
       <>
         <Button
           variant="secondary"
           className='story-reset-btn mt-150 d-flex align-items-center gap-2'
           onClick={() => setShowResetModal(true)}
           aria-label="Reset all story progress">
           <FontAwesomeIcon icon={faUndo} className="fs-4" aria-hidden="true" />
           Reset
          </Button>

         <ResetModal
           show={showResetModal}
           handleClose={() => setShowResetModal(false)}
           handleReset={handleReset}
         />
       </>
     )}
   </div>
 );
};

export default StoryDetails;